import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ICampoCustomizable } from '../interfaces/campo-customizable.interface';
import { CamposCustomizablesService, IOpcionVariable, IOpcionNueva, ICampoCustomizableSaveRequest } from '../services/campos-customizables.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-campo-customizable-form',
  templateUrl: './campo-customizable-form.component.html',
  providers: [MessageService]
})
export class CampoCustomizableFormComponent implements OnInit, OnChanges {
  @Input() model: ICampoCustomizable | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;

  // Propiedades para el modal de agregar opción
  displayAgregarOpcionDialog: boolean = false;
  nuevaOpcion: string = '';

  // Opciones para el dropdown de características (cargadas desde el API)
  caracteristicasOptions: { label: string; value: string }[] = [];
  
  // Array para las opciones creadas (para la tabla) - vacío por defecto
  opcionesCreadas: string[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private camposCustomizablesService: CamposCustomizablesService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadCaracteristicasOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      this.initForm(); // Siempre reinicializar el formulario cuando cambie el modelo
      if (changes['model'].currentValue) {
        this.loadCampoCustomizable();
        this.loadOpcionesNuevas();
      } else {
        this.opcionesCreadas = [];
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      descripcion: [{ value: '', disabled: !!this.model }, [Validators.required]],
      caracteristica: ['', [Validators.required]]
    });
  }

  loadCaracteristicasOptions(): void {
    this.camposCustomizablesService.getOpcionesCaracteristicas().subscribe({
      next: (response) => {
        if (response.success) {
          this.caracteristicasOptions = response.data.map((opcion: IOpcionVariable) => ({
            label: opcion.sOpcion,
            value: opcion.sValor
          }));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de características'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de características'
        });
      }
    });
  }

  loadOpcionesNuevas(): void {
    if (!this.model?.descripcion) return;

    this.camposCustomizablesService.getOpcionesNuevas(this.model.descripcion).subscribe({
      next: (response) => {
        // Cargar las opciones independientemente del success si hay datos
        if (response.data && response.data.length > 0) {
          this.opcionesCreadas = response.data.map((opcion: IOpcionNueva) => opcion.opcion);
        } else {
          this.opcionesCreadas = [];
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones creadas'
        });
      }
    });
  }

  openAgregarOpcionDialog(): void {
    this.nuevaOpcion = '';
    this.displayAgregarOpcionDialog = true;
  }

  onAgregarOpcionDialogClosed(): void {
    this.displayAgregarOpcionDialog = false;
    this.nuevaOpcion = '';
  }

  onAgregarOpcionSaved(result: { success: boolean; message: string }): void {
    if (result?.success) {
      // Recargar las opciones del dropdown después de agregar una nueva
      this.loadCaracteristicasOptions();
      // Recargar las opciones de la tabla
      this.loadOpcionesNuevas();
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: result.message
      });
    }
    this.displayAgregarOpcionDialog = false;
  }

  eliminarOpcion(opcion: string): void {
    Swal.fire({
      title: '¿Está seguro?',
      text: `¿Desea eliminar la opción "${opcion}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      iconColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        if (!this.model?.descripcion) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo identificar el campo padre'
          });
          return;
        }

        this.loading = true;
        this.camposCustomizablesService.deleteOpcion(this.model.descripcion, opcion).subscribe({
          next: (response) => {
            // Verificar si el error es "0" (éxito) independientemente del success
            const errorCode = response.data?.[0]?.error;
            if (errorCode === "0") {
              // Recargar las opciones de la tabla después de eliminar
              this.loadOpcionesNuevas();
              this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Opción eliminada correctamente'
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: errorCode || 'Error al eliminar la opción'
              });
            }
            this.loading = false;
          },
          error: (error) => {
            console.error('Error:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al eliminar la opción'
            });
            this.loading = false;
          }
        });
      }
    });
  }

  loadCampoCustomizable(): void {
    if (!this.model?.descripcion) return;
    
    this.loading = true;
    const request = {
      iAccion: 4,
      sCampo: this.model.descripcion,
      sCaract: '',
      iNumReg: 1
    };

    this.camposCustomizablesService.getCamposCustomizables(request).subscribe({
      next: (response) => {
        if (response.success && response.data.length > 0) {
          const campo = response.data[0];
          this.form.patchValue({
            descripcion: campo.descripcion,
            caracteristica: campo.caracteristica
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo cargar el campo customizable'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar el campo customizable'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload: ICampoCustomizableSaveRequest = {
      iAccion: 1, // Crear
      sCampo: formData.descripcion,
      sCaract: formData.caracteristica,
      iNumReg: 0
    };

    this.camposCustomizablesService.saveCampoCustomizable(payload).subscribe({
      next: (response) => {
        // Verificar si el error es "0" (éxito) independientemente del success
        const errorCode = response.data?.[0]?.error;
        if (errorCode === "0") {
          this.onSave.emit({
            success: true,
            message: 'Campo customizable creado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorCode || 'Error al crear el campo customizable'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear el campo customizable'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload: ICampoCustomizableSaveRequest = {
      iAccion: 2, // Actualizar
      sCampo: formData.descripcion,
      sCaract: formData.caracteristica,
      iNumReg: 0
    };

    this.camposCustomizablesService.saveCampoCustomizable(payload).subscribe({
      next: (response) => {
        // Verificar si el error es "0" (éxito) independientemente del success
        const errorCode = response.data?.[0]?.error;
        if (errorCode === "0") {
          this.onSave.emit({
            success: true,
            message: 'Campo customizable actualizado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorCode || 'Error al actualizar el campo customizable'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el campo customizable'
        });
        this.loading = false;
      }
    });
  }
} 